import {useCallback, useEffect, useState} from 'react'
import {useLocation, useParams} from 'react-router'
import styles from './DialogDetails.module.scss'
import {cx} from '../../../../utils'
import {DialogAnalytics} from '../DialogAnalytics/DialogAnalytics'
import {DialogSetup} from '../DialogSetup/DialogSetup'
import {DialogModeration} from '../DialogModeration/DialogModeration'
import {WuIcon} from '@npm-questionpro/wick-ui-lib'
import {dialogApi} from '../../../../api/dialogApi'
import {useWt} from '@npm-questionpro/wick-ui-lib'
import {DIALOG} from '../../../../Translation/English/dialog'

type TTabType = 'moderation' | 'analytics' | 'setup'

interface ILocationState {
  switchToSetupTab?: boolean
}

export const DialogDetails: React.FC = () => {
  const location = useLocation()
  const {switchToSetupTab} = (location.state as ILocationState) || {}
  const {dialogId} = useParams<{dialogId: string}>()

  const wt = useWt()
  const [isActiveTab, setIsActiveTab] = useState<TTabType>('moderation')
  const [showViolations, setShowViolations] = useState<boolean>(false)
  const {data: flaggedResponses} = dialogApi.useFlaggedResponses(dialogId ?? '')
  const {data: dialogDetails} = dialogApi.useDialogDetails(dialogId ?? '')

  const flaggedCount =
    [
      ...(flaggedResponses?.responses || []),
      ...(flaggedResponses?.replies || []),
    ].length || 0

  const changeTab = useCallback((tab: TTabType) => {
    setIsActiveTab(tab)
  }, [])

  useEffect(() => {
    if (switchToSetupTab) {
      setIsActiveTab('setup')
    }
  }, [switchToSetupTab])

  const getTabDetails = (): React.ReactElement | undefined => {
    switch (isActiveTab) {
      case 'moderation':
        return (
          <DialogModeration
            showViolations={showViolations}
            setShowViolations={setShowViolations}
          />
        )
      case 'analytics':
        return <DialogAnalytics />
      case 'setup':
        return <DialogSetup />
    }
  }

  return (
    <div className={styles.dialogDetails}>
      <div className={styles.tabs}>
        <div
          className={cx(
            styles.tab,
            isActiveTab === 'moderation' && styles.tabOpen,
          )}
          onClick={() => changeTab('moderation')}
        >
          {wt(DIALOG.moderation)}

          {flaggedCount > 0 && (
            <span
              className={styles.dialogDetailsModerationBtn}
              onClick={() => setShowViolations(true)}
            >
              ({flaggedCount || dialogDetails?.restrictedUsers?.length})
            </span>
          )}
          {flaggedCount === 0 &&
            (dialogDetails?.restrictedUsers?.length ?? 0) > 0 && (
              <WuIcon
                icon="wm-warning"
                data-testid="moderation-violations-badge"
                className={cx(
                  styles.dialogDetailsModerationBtn,
                  styles.dialogDetailsModerationIcon,
                )}
                onClick={() => setShowViolations(true)}
              />
            )}
        </div>
        <div
          className={cx(
            styles.tab,
            isActiveTab === 'analytics' && styles.tabOpen,
          )}
          onClick={() => changeTab('analytics')}
        >
          {wt(DIALOG.analytics)}
        </div>
        <div
          className={cx(styles.tab, isActiveTab === 'setup' && styles.tabOpen)}
          onClick={() => changeTab('setup')}
        >
          {wt(DIALOG.setup)}
        </div>
      </div>
      {getTabDetails()}
    </div>
  )
}
